import { IQuiz, StepRouteMap } from '@quizzach/interfaces';

/**
 * Returns the question number after which the halftime break should be placed,
 * or null when halftime cannot be offered for this quiz.
 *
 * - Quizzes without chapters: the natural midpoint of the regular questions.
 * - Quizzes with chapters: the question nearest that midpoint after which the
 *   quiz can be split without cutting through a chapter (no chapter spans the
 *   gap between the split question and the next). When chapters tile every
 *   question this is the chapter boundary closest to the middle; when there are
 *   chapter-free stretches the natural midpoint is usually itself a valid split.
 *   Returns null only when no clean split exists (e.g. a single chapter spanning
 *   the whole quiz), and halftime is not offered.
 *
 * The trailing estimation question is excluded from the count.
 */
export function getHalftimeQuestion(quiz: IQuiz | undefined): number | null {
	const numQuestions = quiz?.questions.length ?? 0;
	// The last question is the estimation question and is never part of a half.
	const regularQuestions = numQuestions - 1;
	if (regularQuestions < 2) {
		return null;
	}

	const naturalHalf = Math.ceil(regularQuestions / 2);
	const chapters = quiz?.premade_quiz?.chapters ?? [];
	if (chapters.length === 0) {
		return naturalHalf;
	}

	// A split after question N is "clean" only when no chapter spans the gap
	// between N and N+1 - otherwise the halftime break would land inside a
	// chapter. When chapters tile every question the only clean points are
	// chapter ends; when there are chapter-free stretches the natural midpoint
	// is usually clean on its own.
	const cutsThroughChapter = (n: number): boolean =>
		chapters.some(
			(chapter) => chapter.chapter_start <= n && n < chapter.chapter_end
		);

	// Walk candidates in order and keep the one nearest the natural half; on a
	// tie the earlier split wins (the ascending loop reaches it first).
	let bestSplit: number | null = null;
	for (let n = 1; n < regularQuestions; n++) {
		if (cutsThroughChapter(n)) {
			continue;
		}
		if (
			bestSplit === null ||
			Math.abs(n - naturalHalf) < Math.abs(bestSplit - naturalHalf)
		) {
			bestSplit = n;
		}
	}

	return bestSplit;
}

export function generateQuizStepMap(
	quiz: IQuiz | undefined,
	quizMode: 'qaqa' | 'qqaa',
	halftime: boolean
): { stepMap: StepRouteMap; pathIndexMap: Record<string, number> } {
	const stepMap: StepRouteMap = {};
	const pathIndexMap: Record<string, number> = {};
	let stepCounter = 1;

	const uuid = quiz?.uuid;
	const numQuestions = quiz?.questions.length || 0;

	// Special redirect for specific premade quiz UUID
	const finalNext =
		quiz?.premade_quiz?.uuid == '019af8be-8b31-7fb8-b0c8-7597538c95e9'
			? '/premade/quiz/019af93c-cc54-7963-a3a5-0211e17451ef'
			: quiz?.premade_quiz?.puzzle
				? '/premade/tag/poveznica'
				: '/profile';
	const chapters = quiz?.premade_quiz?.chapters ?? [];
	const hasChapters =
		quiz?.premade_quiz?.chapters && quiz.premade_quiz.chapters.length > 0;
	const lastRegularQuestionNumber = numQuestions - 1;

	// Kids quizzes never play the trailing estimation question: after the last
	// regular answer the quiz goes straight to the end screen, in every mode.
	const skipEstimation = quiz?.premade_quiz?.for_kids === true;

	// Puzzle quizzes never use halftime, whatever the stored preference is.
	if (quiz?.premade_quiz?.puzzle) {
		halftime = false;
	}

	// Chapters are only supported in QQAA mode; QAQA falls back to QQAA.
	if (hasChapters && quizMode === 'qaqa') {
		quizMode = 'qqaa';
	}

	// Halftime split point for chaptered quizzes, aligned to a chapter boundary
	// so the break never lands inside a chapter. null = no chapter-aware halftime
	// (halftime disabled, no chapters, or no usable boundary). Chapterless
	// halftime keeps using its own dedicated branch below.
	const halftimeSplit =
		halftime && quizMode === 'qqaa' && hasChapters
			? getHalftimeQuestion(quiz)
			: null;

	if (!uuid || numQuestions === 0) {
		return { stepMap: {}, pathIndexMap: {} };
	}

	const addStep = (
		prev: string,
		current: string,
		next: string,
		type: StepRouteMap[number]['type']
	) => {
		stepMap[stepCounter] = { prev, current, next, type };
		pathIndexMap[current] = stepCounter;
		stepCounter++;
	};

	const getChapterNumberByStart = (questionNumber: number): number | null => {
		if (!hasChapters) return null;
		const chapterIndex = chapters.findIndex(
			(chapter) => chapter.chapter_start === questionNumber
		);
		return chapterIndex >= 0 ? chapterIndex + 1 : null;
	};

	const getChapterNumberByEnd = (questionNumber: number): number | null => {
		if (!hasChapters) return null;
		const chapterIndex = chapters.findIndex(
			(chapter) => chapter.chapter_end === questionNumber
		);
		return chapterIndex >= 0 ? chapterIndex + 1 : null;
	};

	const getQuestionChapterEndRoute = (
		questionNumber: number
	): string | null => {
		const chapterNumber = getChapterNumberByEnd(questionNumber);
		return chapterNumber
			? `/quiz/${uuid}/question-chapter-end/${chapterNumber}`
			: null;
	};

	const getAnswerChapterEndRoute = (questionNumber: number): string | null => {
		const chapterNumber = getChapterNumberByEnd(questionNumber);
		return chapterNumber
			? `/quiz/${uuid}/answer-chapter-end/${chapterNumber}`
			: null;
	};

	const getQuestionRouteAfterChapterEnd = (questionNumber: number): string => {
		// First half ends here: go to the first-half answers via the mid-break.
		if (halftimeSplit !== null && questionNumber === halftimeSplit) {
			return `/quiz/${uuid}/first-half-mid-break`;
		}
		if (questionNumber < lastRegularQuestionNumber) {
			const nextQuestionNumber = questionNumber + 1;
			const nextChapterNumber = getChapterNumberByStart(nextQuestionNumber);
			return nextChapterNumber
				? `/quiz/${uuid}/question-chapter/${nextChapterNumber}`
				: `/quiz/${uuid}/question/${nextQuestionNumber}`;
		}

		// All questions are done: with halftime the second half reveals its
		// answers, otherwise the single questions-to-answers break is used.
		return halftimeSplit !== null
			? `/quiz/${uuid}/second-half-mid-break`
			: `/quiz/${uuid}/break`;
	};

	const getFinalAnswerRoute = () =>
		skipEstimation ? `/quiz/${uuid}/end` : `/quiz/${uuid}/estimation-intro`;

	const getAnswerRouteAfterChapterEnd = (questionNumber: number): string => {
		// First half's answers are done: take the halftime break.
		if (halftimeSplit !== null && questionNumber === halftimeSplit) {
			return `/quiz/${uuid}/halftime`;
		}
		if (questionNumber < lastRegularQuestionNumber) {
			const nextQuestionNumber = questionNumber + 1;
			const nextChapterNumber = getChapterNumberByStart(nextQuestionNumber);
			return nextChapterNumber
				? `/quiz/${uuid}/answer-chapter/${nextChapterNumber}`
				: `/quiz/${uuid}/answer/${nextQuestionNumber}`;
		}

		return getFinalAnswerRoute();
	};

	if (quizMode === 'qaqa') {
		for (let i = 1; i < numQuestions; i++) {
			addStep(
				i === 1 ? `/quiz/${uuid}/start` : `/quiz/${uuid}/answer/${i - 1}`,
				`/quiz/${uuid}/question/${i}`,
				`/quiz/${uuid}/answer/${i}`,
				'question'
			);
			addStep(
				`/quiz/${uuid}/question/${i}`,
				`/quiz/${uuid}/answer/${i}`,
				i === numQuestions - 1
					? getFinalAnswerRoute()
					: `/quiz/${uuid}/question/${i + 1}`,
				'answer'
			);
		}
	} else if (quizMode === 'qqaa') {
		if (halftime && !hasChapters) {
			const halfLength = Math.ceil((numQuestions - 1) / 2);

			for (let i = 1; i <= halfLength; i++) {
				addStep(
					i === 1 ? `/quiz/${uuid}/start` : `/quiz/${uuid}/question/${i - 1}`,
					`/quiz/${uuid}/question/${i}`,
					i < halfLength
						? `/quiz/${uuid}/question/${i + 1}`
						: `/quiz/${uuid}/first-half-mid-break`,
					'question'
				);
			}

			addStep(
				`/quiz/${uuid}/question/${halfLength}`,
				`/quiz/${uuid}/first-half-mid-break`,
				`/quiz/${uuid}/answer/1`,
				'break'
			);

			for (let i = 1; i <= halfLength; i++) {
				addStep(
					i === 1
						? `/quiz/${uuid}/first-half-mid-break`
						: `/quiz/${uuid}/answer/${i - 1}`,
					`/quiz/${uuid}/answer/${i}`,
					i < halfLength
						? `/quiz/${uuid}/answer/${i + 1}`
						: `/quiz/${uuid}/halftime`,
					'answer'
				);
			}

			addStep(
				`/quiz/${uuid}/answer/${halfLength}`,
				`/quiz/${uuid}/halftime`,
				`/quiz/${uuid}/question/${halfLength + 1}`,
				'break'
			);

			for (let i = halfLength + 1; i < numQuestions; i++) {
				addStep(
					i === halfLength + 1
						? `/quiz/${uuid}/halftime`
						: `/quiz/${uuid}/question/${i - 1}`,
					`/quiz/${uuid}/question/${i}`,
					i < numQuestions - 1
						? `/quiz/${uuid}/question/${i + 1}`
						: `/quiz/${uuid}/second-half-mid-break`,
					'question'
				);
			}

			addStep(
				`/quiz/${uuid}/question/${numQuestions - 1}`,
				`/quiz/${uuid}/second-half-mid-break`,
				`/quiz/${uuid}/answer/${halfLength + 1}`,
				'break'
			);

			for (let i = halfLength + 1; i < numQuestions; i++) {
				addStep(
					i === halfLength + 1
						? `/quiz/${uuid}/second-half-mid-break`
						: `/quiz/${uuid}/answer/${i - 1}`,
					`/quiz/${uuid}/answer/${i}`,
					i < numQuestions - 1
						? `/quiz/${uuid}/answer/${i + 1}`
						: getFinalAnswerRoute(),
					'answer'
				);
			}
		} else {
			// Helper function to check if question i starts a chapter
			const getChapterForQuestion = (i: number): number | null => {
				return getChapterNumberByStart(i);
			};

			// Helper function to get previous step for question i
			const getPrevForQuestion = (i: number): string => {
				if (i === 1) {
					if (quiz?.premade_quiz?.puzzle) {
						return `/quiz/${uuid}/puzzle-intro`;
					}
					// Check if first question starts a chapter
					const chapterNum = getChapterForQuestion(1);
					if (chapterNum) {
						return `/quiz/${uuid}/question-chapter/${chapterNum}`;
					}
					return `/quiz/${uuid}/start`;
				}
				// First question of the second half (when it is not itself a chapter
				// start) comes straight after the halftime break.
				if (halftimeSplit !== null && i === halftimeSplit + 1) {
					return `/quiz/${uuid}/halftime`;
				}
				// Check if current question starts a chapter
				const chapterNum = getChapterForQuestion(i);
				if (chapterNum) {
					// Previous step is the chapter intro
					return `/quiz/${uuid}/question-chapter/${chapterNum}`;
				}
				const previousChapterEndRoute = getQuestionChapterEndRoute(i - 1);
				if (previousChapterEndRoute) {
					return previousChapterEndRoute;
				}
				return `/quiz/${uuid}/question/${i - 1}`;
			};

			// Helper function to get next step for question i
			const getNextForQuestion = (i: number): string => {
				const currentChapterEndRoute = getQuestionChapterEndRoute(i);
				if (currentChapterEndRoute) {
					return currentChapterEndRoute;
				}
				// Halftime split that is not itself a chapter end: hand off to the
				// first half's answers via the mid-break.
				if (halftimeSplit !== null && i === halftimeSplit) {
					return `/quiz/${uuid}/first-half-mid-break`;
				}
				if (i < numQuestions - 1) {
					// Check if next question starts a chapter
					const nextChapterNum = getChapterForQuestion(i + 1);
					if (nextChapterNum) {
						return `/quiz/${uuid}/question-chapter/${nextChapterNum}`;
					}
					return `/quiz/${uuid}/question/${i + 1}`;
				}
				return halftimeSplit !== null
					? `/quiz/${uuid}/second-half-mid-break`
					: `/quiz/${uuid}/break`;
			};

			// Helper function to check if answer i starts a chapter
			const getChapterForAnswer = (i: number): number | null => {
				return getChapterNumberByStart(i);
			};

			// Helper function to get previous step for answer i
			const getPrevForAnswer = (i: number): string => {
				if (i === 1) {
					// Check if first answer starts a chapter
					const chapterNum = getChapterForAnswer(1);
					if (chapterNum) {
						return `/quiz/${uuid}/answer-chapter/${chapterNum}`;
					}
					if (halftimeSplit !== null) {
						return `/quiz/${uuid}/first-half-mid-break`;
					}
					return quiz?.premade_quiz?.puzzle
						? `/quiz/${uuid}/puzzle-answer`
						: `/quiz/${uuid}/break`;
				}
				// First answer of the second half (when it is not itself a chapter
				// start) comes straight after the second-half mid-break.
				if (halftimeSplit !== null && i === halftimeSplit + 1) {
					return `/quiz/${uuid}/second-half-mid-break`;
				}
				// Check if current answer starts a chapter
				const chapterNum = getChapterForAnswer(i);
				if (chapterNum) {
					// Previous step is the chapter intro
					return `/quiz/${uuid}/answer-chapter/${chapterNum}`;
				}
				const previousChapterEndRoute = getAnswerChapterEndRoute(i - 1);
				if (previousChapterEndRoute) {
					return previousChapterEndRoute;
				}
				return `/quiz/${uuid}/answer/${i - 1}`;
			};

			// Helper function to get next step for answer i
			const getNextForAnswer = (i: number): string => {
				const currentChapterEndRoute = getAnswerChapterEndRoute(i);
				if (currentChapterEndRoute) {
					return currentChapterEndRoute;
				}
				// Halftime split that is not itself a chapter end: take the halftime
				// break after the first half's answers.
				if (halftimeSplit !== null && i === halftimeSplit) {
					return `/quiz/${uuid}/halftime`;
				}
				if (i < numQuestions - 1) {
					// Check if next answer starts a chapter
					const nextChapterNum = getChapterForAnswer(i + 1);
					if (nextChapterNum) {
						return `/quiz/${uuid}/answer-chapter/${nextChapterNum}`;
					}
					return `/quiz/${uuid}/answer/${i + 1}`;
				}
				return getFinalAnswerRoute();
			};

			// Add puzzle intro if needed
			if (quiz?.premade_quiz?.puzzle) {
				const firstChapterNum = getChapterForQuestion(1);
				addStep(
					`/quiz/${uuid}/start`,
					`/quiz/${uuid}/puzzle-intro`,
					firstChapterNum
						? `/quiz/${uuid}/question-chapter/${firstChapterNum}`
						: `/quiz/${uuid}/question/1`,
					'break'
				);
			}

			// Add question chapter intros before questions that start chapters
			if (hasChapters) {
				quiz?.premade_quiz?.chapters?.forEach((chapter, index) => {
					const chapterNumber = index + 1;
					let prevStep: string;

					if (
						halftimeSplit !== null &&
						chapter.chapter_start === halftimeSplit + 1
					) {
						// First chapter of the second half comes right after halftime.
						prevStep = `/quiz/${uuid}/halftime`;
					} else if (chapter.chapter_start === 1) {
						// First chapter - comes after start or puzzle-intro
						prevStep = quiz?.premade_quiz?.puzzle
							? `/quiz/${uuid}/puzzle-intro`
							: `/quiz/${uuid}/start`;
					} else {
						// Later chapter - comes after previous question or chapter end
						prevStep =
							getQuestionChapterEndRoute(chapter.chapter_start - 1) ??
							`/quiz/${uuid}/question/${chapter.chapter_start - 1}`;
					}

					addStep(
						prevStep,
						`/quiz/${uuid}/question-chapter/${chapterNumber}`,
						`/quiz/${uuid}/question/${chapter.chapter_start}`,
						'question-chapter'
					);
				});
			}

			// Add question chapter end breaks after questions that end chapters
			if (hasChapters) {
				chapters.forEach((chapter, index) => {
					if (
						chapter.chapter_end < 1 ||
						chapter.chapter_end > lastRegularQuestionNumber
					) {
						return;
					}

					addStep(
						`/quiz/${uuid}/question/${chapter.chapter_end}`,
						`/quiz/${uuid}/question-chapter-end/${index + 1}`,
						getQuestionRouteAfterChapterEnd(chapter.chapter_end),
						'question-chapter-end'
					);
				});
			}

			// Add question steps
			for (let i = 1; i < numQuestions; i++) {
				// Skip adding question step if it starts a chapter (chapter step already added)
				const chapterNum = getChapterForQuestion(i);
				if (!chapterNum) {
					addStep(
						getPrevForQuestion(i),
						`/quiz/${uuid}/question/${i}`,
						getNextForQuestion(i),
						'question'
					);
				} else {
					// Question starts a chapter, so prev is chapter intro, next is next question or chapter
					addStep(
						`/quiz/${uuid}/question-chapter/${chapterNum}`,
						`/quiz/${uuid}/question/${i}`,
						getNextForQuestion(i),
						'question'
					);
				}
			}

			if (halftimeSplit !== null) {
				const firstAnswerChapterNum = getChapterForAnswer(1);
				// The second half always opens with a chapter (the split is a
				// boundary); fall back to plain question/answer routes defensively.
				const secondHalfChapterNum = getChapterNumberByStart(halftimeSplit + 1);

				// After the first half's questions, reveal the first half's answers.
				addStep(
					getQuestionChapterEndRoute(halftimeSplit) ??
						`/quiz/${uuid}/question/${halftimeSplit}`,
					`/quiz/${uuid}/first-half-mid-break`,
					firstAnswerChapterNum
						? `/quiz/${uuid}/answer-chapter/${firstAnswerChapterNum}`
						: `/quiz/${uuid}/answer/1`,
					'break'
				);

				// The halftime break separates the two halves.
				addStep(
					getAnswerChapterEndRoute(halftimeSplit) ??
						`/quiz/${uuid}/answer/${halftimeSplit}`,
					`/quiz/${uuid}/halftime`,
					secondHalfChapterNum
						? `/quiz/${uuid}/question-chapter/${secondHalfChapterNum}`
						: `/quiz/${uuid}/question/${halftimeSplit + 1}`,
					'break'
				);

				// After the second half's questions, reveal the second half's answers.
				addStep(
					getQuestionChapterEndRoute(lastRegularQuestionNumber) ??
						`/quiz/${uuid}/question/${lastRegularQuestionNumber}`,
					`/quiz/${uuid}/second-half-mid-break`,
					secondHalfChapterNum
						? `/quiz/${uuid}/answer-chapter/${secondHalfChapterNum}`
						: `/quiz/${uuid}/answer/${halftimeSplit + 1}`,
					'break'
				);
			} else if (quiz?.premade_quiz?.puzzle) {
				const questionBreakPrev =
					getQuestionChapterEndRoute(numQuestions - 1) ??
					`/quiz/${uuid}/question/${numQuestions - 1}`;
				addStep(
					questionBreakPrev,
					`/quiz/${uuid}/break`,
					`/quiz/${uuid}/puzzle-answer`,
					'break'
				);

				const firstAnswerChapterNum = getChapterForAnswer(1);
				addStep(
					`/quiz/${uuid}/break`,
					`/quiz/${uuid}/puzzle-answer`,
					firstAnswerChapterNum
						? `/quiz/${uuid}/answer-chapter/${firstAnswerChapterNum}`
						: `/quiz/${uuid}/answer/1`,
					'break'
				);
			} else {
				const firstAnswerChapterNum = getChapterForAnswer(1);
				const questionBreakPrev =
					getQuestionChapterEndRoute(numQuestions - 1) ??
					`/quiz/${uuid}/question/${numQuestions - 1}`;
				addStep(
					questionBreakPrev,
					`/quiz/${uuid}/break`,
					firstAnswerChapterNum
						? `/quiz/${uuid}/answer-chapter/${firstAnswerChapterNum}`
						: `/quiz/${uuid}/answer/1`,
					'break'
				);
			}

			// Add answer chapter intros before answers that start chapters
			if (hasChapters) {
				quiz?.premade_quiz?.chapters?.forEach((chapter, index) => {
					const chapterNumber = index + 1;
					let prevStep: string;

					if (
						halftimeSplit !== null &&
						chapter.chapter_start === halftimeSplit + 1
					) {
						// First answer chapter of the second half follows its mid-break.
						prevStep = `/quiz/${uuid}/second-half-mid-break`;
					} else if (chapter.chapter_start === 1) {
						// First answer chapter - comes after the (first-half) break or
						// puzzle-answer
						prevStep =
							halftimeSplit !== null
								? `/quiz/${uuid}/first-half-mid-break`
								: quiz?.premade_quiz?.puzzle
									? `/quiz/${uuid}/puzzle-answer`
									: `/quiz/${uuid}/break`;
					} else {
						// Later answer chapter - comes after previous answer or chapter end
						prevStep =
							getAnswerChapterEndRoute(chapter.chapter_start - 1) ??
							`/quiz/${uuid}/answer/${chapter.chapter_start - 1}`;
					}

					addStep(
						prevStep,
						`/quiz/${uuid}/answer-chapter/${chapterNumber}`,
						`/quiz/${uuid}/answer/${chapter.chapter_start}`,
						'answer-chapter'
					);
				});
			}

			// Add answer chapter end breaks after answers that end chapters
			if (hasChapters) {
				chapters.forEach((chapter, index) => {
					if (
						chapter.chapter_end < 1 ||
						chapter.chapter_end > lastRegularQuestionNumber
					) {
						return;
					}

					addStep(
						`/quiz/${uuid}/answer/${chapter.chapter_end}`,
						`/quiz/${uuid}/answer-chapter-end/${index + 1}`,
						getAnswerRouteAfterChapterEnd(chapter.chapter_end),
						'answer-chapter-end'
					);
				});
			}

			// Add answer steps
			for (let i = 1; i < numQuestions; i++) {
				const chapterNum = getChapterForAnswer(i);
				if (!chapterNum) {
					addStep(
						getPrevForAnswer(i),
						`/quiz/${uuid}/answer/${i}`,
						getNextForAnswer(i),
						'answer'
					);
				} else {
					// Answer starts a chapter, so prev is chapter intro, next is next answer or chapter
					addStep(
						`/quiz/${uuid}/answer-chapter/${chapterNum}`,
						`/quiz/${uuid}/answer/${i}`,
						getNextForAnswer(i),
						'answer'
					);
				}
			}
		}
	}
	if (skipEstimation) {
		addStep(
			getAnswerChapterEndRoute(numQuestions - 1) ??
				`/quiz/${uuid}/answer/${numQuestions - 1}`,
			`/quiz/${uuid}/end`,
			finalNext,
			'break'
		);
		return { stepMap, pathIndexMap };
	}
	addStep(
		getAnswerChapterEndRoute(numQuestions - 1) ??
			`/quiz/${uuid}/answer/${numQuestions - 1}`,
		`/quiz/${uuid}/estimation-intro`,
		`/quiz/${uuid}/question/${numQuestions}`,
		'break'
	);
	addStep(
		`/quiz/${uuid}/estimation-intro`,
		`/quiz/${uuid}/question/${numQuestions}`,
		`/quiz/${uuid}/answer/${numQuestions}`,
		'question'
	);
	addStep(
		`/quiz/${uuid}/question/${numQuestions}`,
		`/quiz/${uuid}/answer/${numQuestions}`,
		`/quiz/${uuid}/end`,
		'answer'
	);
	addStep(
		`/quiz/${uuid}/answer/${numQuestions}`,
		`/quiz/${uuid}/end`,
		finalNext,
		'break'
	);

	return { stepMap, pathIndexMap };
}
